/**
 * @fileoverview Retrieve authorized identity/selfie evidence for the selected portal review context. The backend owns access, image identities and fetch audits. Successful photos remain visible if another download fails; `onReady` admits Zoom comparison only when all required kinds downloaded. Zoom completion independently requires the assigned verifier's current-assignment ID/selfie views (`payment_verification_workflow.py#confirm_evidence`). Blob URLs are transient. Viewing images does not accept verification or authorize banking changes.
 */
import React, { useEffect, useRef, useState } from 'react';
import api from '../../api';
import { AppButton, AppIcon, AppModal } from '../shared';
import './IdentityImages.css';

const LABELS = { document_front: 'Photo ID · front', document_back: 'Photo ID · back', selfie: 'Selfie' };

 
export default function IdentityImages({ reviewId, matterNumber, clientId, canView, onReady, requiredKinds = ['selfie'] }) {
  const [images, setImages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const enlargedImage = useRef(null);
  const requiredKey = requiredKinds.join(',');
  const query = matterNumber && clientId ? `?matter_number=${encodeURIComponent(matterNumber)}&client_id=${encodeURIComponent(clientId)}` : '';
  useEffect(() => {
    let active = true; const objectUrls = [];
    setImages([]); setSelected(null); setError(''); onReady?.(false);
    if (!canView) return undefined;
    setLoading(true);
    const base = `/payment-instructions/${encodeURIComponent(reviewId)}/identity-images`;
    api.get(base + query).then(async ({ data }) => {
      if (!active) return;
      const metadata = data.images || [];
      const blobs = await Promise.allSettled(metadata.map(image => api.get(`${base}/${encodeURIComponent(image.id)}${query}`, { responseType: 'blob' })));
      if (!active) return;
      const loaded = metadata.flatMap((image, index) => {
        if (blobs[index].status !== 'fulfilled') return [];
        const url = URL.createObjectURL(blobs[index].value.data); objectUrls.push(url);
        return { ...image, url };
      });
      onReady?.(requiredKey.split(',').every(kind => loaded.some(image => image.kind === kind)));
      setImages(loaded);
      if (loaded.length !== metadata.length) setError('Some verification images could not be loaded.');
    }).catch(failure => { if (active) setError(failure.response?.data?.error || 'Verification images could not be loaded.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; objectUrls.forEach(url => URL.revokeObjectURL(url)); };
  }, [reviewId, query, canView, refresh, onReady, requiredKey]);
  useEffect(() => {
    if (!selected) return undefined;
    const opener = document.activeElement;
    const close = enlargedImage.current?.closest('[role="dialog"]')?.querySelector('button');
    close?.focus();
    function handleKey(event) {
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopImmediatePropagation(); setSelected(null);
      } else if (event.key === 'Tab') {
        event.preventDefault(); event.stopImmediatePropagation(); close?.focus();
      }
    }
    document.addEventListener('keydown', handleKey, true);
    return () => {
      document.removeEventListener('keydown', handleKey, true);
      if (opener?.isConnected) opener.focus();
    };
  }, [selected]);
  if (!canView) return null;
  return <section className="identity-images" aria-label="Stripe verification images">
    <h3><AppIcon icon="pi pi-image" aria-hidden="true" /> ID and selfie</h3>
    {loading ? <p role="status">Loading verification images…</p> : null}
    <div className="identity-images__grid">{images.map(image => <button key={image.id} type="button"
      className="identity-images__card" onClick={() => setSelected(image)} aria-label={`View ${LABELS[image.kind]}`}>
      <img src={image.url} alt={LABELS[image.kind]} />
      <span>{LABELS[image.kind]} <AppIcon icon="pi pi-search" aria-hidden="true" /></span>
    </button>)}</div>
    {error ? <div className="identity-images__unavailable"><span role="status"><AppIcon icon="pi pi-exclamation-triangle" aria-hidden="true" /> Images unavailable</span><p>{error}</p><AppButton variant="secondary" icon="pi pi-refresh" onClick={() => setRefresh(value => value + 1)}>Retry images</AppButton></div> : null}
    {!loading && !error && !images.length ? <p>No verification images available.</p> : null}
    <AppModal visible={!!selected} title={selected ? LABELS[selected.kind] : 'Verification image'} onClose={() => setSelected(null)} width={1000}>
      {selected ? <img ref={enlargedImage} className="identity-images__full" src={selected.url} alt={LABELS[selected.kind]} /> : null}
    </AppModal>
  </section>;
}
